import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { Bug, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FeedbackStatusChip, FEEDBACK_STATUS_LABELS } from "@/components/feedback/FeedbackStatusChip";

const fmt = d => { try { return format(parseISO(d), "d MMM yyyy HH:mm"); } catch { return d; } };

export function FeedbackTab() {
  const [items, setItems] = useState(null);
  const [statusF, setStatusF] = useState("active");
  const [kindF, setKindF] = useState("all");
  const load = () => api.get("/admin/feedback").then(r => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => (items || []).filter(f =>
    (kindF === "all" || f.kind === kindF)
    && (statusF === "all" || (statusF === "active" ? !["done", "declined"].includes(f.status) : f.status === statusF))
  ), [items, statusF, kindF]);

  return (
    <div className="space-y-4" data-testid="admin-feedback-tab">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">App settings</div>
        <h2 className="font-display font-black text-3xl mt-1">App Improvement Requests</h2>
        <p className="opacity-60 text-sm mt-1">Bugs and suggestions raised by users. Set a status and leave a note — the user sees both on their request.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusF} onValueChange={setStatusF}><SelectTrigger className="h-9 w-40" data-testid="feedback-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="active">Active (not done)</SelectItem><SelectItem value="all">All statuses</SelectItem>{Object.entries(FEEDBACK_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        <Select value={kindF} onValueChange={setKindF}><SelectTrigger className="h-9 w-36" data-testid="feedback-filter-kind"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Bugs + ideas</SelectItem><SelectItem value="bug">Bugs</SelectItem><SelectItem value="improvement">Improvements</SelectItem></SelectContent></Select>
        <span className="text-[10px] tracking-widest uppercase opacity-50 ml-auto" data-testid="feedback-count">{rows.length} of {(items || []).length}</span>
      </div>
      {items === null ? <div className="opacity-50 font-display tracking-widest">LOADING…</div>
        : rows.length === 0 ? <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="admin-feedback-empty">No requests match.</div>
        : <div className="space-y-2" data-testid="admin-feedback-list">{rows.map(f => <FeedbackRow key={f.id} f={f} onChanged={load} />)}</div>}
    </div>
  );
}

function FeedbackRow({ f, onChanged }) {
  const [note, setNote] = useState(f.admin_note || "");
  const [busy, setBusy] = useState(false);
  const save = async (status) => {
    setBusy(true);
    try { await api.post(`/admin/feedback/${f.id}/status`, { status, admin_note: note }); toast.success("Updated"); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="card-tactical rounded-md p-4" data-testid={`admin-feedback-${f.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {f.kind === "bug" ? <Bug className="w-4 h-4 text-red-400" /> : <Lightbulb className="w-4 h-4 text-[#83B68B]" />}
        <span className="font-semibold">{f.title}</span>
        <FeedbackStatusChip status={f.status} />
        <span className="text-xs opacity-50 ml-auto">{f.user_name} · {fmt(f.created_at)}{f.page ? ` · ${f.page}` : ""}</span>
      </div>
      <p className="text-sm opacity-80 mt-2 whitespace-pre-wrap">{f.details}</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto] items-start">
        <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Note back to the user (optional)" data-testid={`admin-feedback-note-${f.id}`} />
        <Select value={f.status} onValueChange={save} disabled={busy}><SelectTrigger className="h-9 w-40" data-testid={`admin-feedback-status-${f.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(FEEDBACK_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      </div>
      {note !== (f.admin_note || "") && <div className="flex justify-end mt-2"><button type="button" onClick={() => save(f.status)} disabled={busy} data-testid={`admin-feedback-save-note-${f.id}`} className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] disabled:opacity-40">Save note</button></div>}
    </div>
  );
}
